// pages/Notifications.jsx
//
// System-wide notification centre. One feed for everything that comes into
// AXO (briefs, replies), goes out of it (emails, SMS/WhatsApp, LinkedIn/call
// logs), and changes inside it (builds, QA, payments, users, releases).
// Reads /api/notifications, which is scoped server-side; super admins see
// every workspace and can broadcast a system announcement.
//
// Layout mirrors the other super-admin/customer pages (FormSubmissions,
// QAQueue, CompanyPool) and uses the shared Arc component library for all
// controls, dialogs, loading and tabs.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowDownLeft,
  ArrowUpRight,
  Bell,
  Check,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Info,
  Megaphone,
  RefreshCw,
  Settings,
} from "lucide-react";
import apiService from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../arc/components/button/button";
import { Tabs, TabsList, TabsTrigger } from "../arc/components/tabs/tabs";
import { Select } from "../arc/components/select/select";
import { Input } from "../arc/components/input/input";
import { Textarea } from "../arc/components/textarea/textarea";
import { Dialog, DialogContent } from "../arc/components/dialog/dialog";
import { Skeleton } from "../arc/components/skeleton/skeleton";
import { SearchField } from "../arc/components/search-field/search-field";
import { Switch } from "../arc/components/switch/switch";
import { ThemeSwitch } from "../arc/components/theme-switch/theme-switch";
import { useToastStack } from "../arc/components/toast-stack/toast-stack";
import { applyThemeWithTransition } from "../utils/themeTransition";
import { CAP, PLAN_LABELS, normalisePlanId } from "../config/planAccess";

const PAGE_SIZE = 25;

const CATEGORY_LABELS = {
  all: "All categories",
  brief: "Briefs",
  email: "Emails",
  reply: "Replies",
  website: "Websites",
  qa: "QA",
  payment: "Payments",
  outreach: "Outreach",
  activity: "Activity",
  user: "Users",
  system: "System",
};

const DIRECTION_LABELS = {
  all: "All",
  inbound: "Inbound",
  outbound: "Outbound",
  system: "System",
};

const SEVERITY_LABELS = {
  all: "All severities",
  info: "Info",
  success: "Success",
  warning: "Warning",
  error: "Error",
};

// Super-admin plan filter. "Manual" is a customer with no planId (custom
// arrangement), not a plan in its own right.
const PLAN_FILTER_OPTIONS = [
  { value: "all", label: "All plans" },
  ...Object.entries(PLAN_LABELS).map(([value, label]) => ({
    value,
    label,
  })),
  { value: "manual", label: "Manual / custom" },
];

// Announcement audience → the plan capability a workspace needs to see it.
const AUDIENCE_OPTIONS = [
  { value: "all", label: "All plans", capability: null },
  { value: "grow", label: "Grow and above", capability: CAP.CRM_SYNC },
  {
    value: "ai",
    label: "AI and above",
    capability: CAP.REVENUE_INTELLIGENCE,
  },
];

const customerPlanLabel = (customer) => {
  if (!customer) return "Platform-wide";
  if (!customer.planId) return "Manual / custom";
  return PLAN_LABELS[normalisePlanId(customer.planId)] || customer.planId;
};

const capabilityAccessLabel = (capability) => {
  if (!capability) return "All plans";
  if (capability === CAP.CRM_SYNC) return "Grow and above";
  if (capability === CAP.REVENUE_INTELLIGENCE) return "AI and above";
  return capability;
};

// Where an activity notification should take the user when they click Open.
// Older rows may have been stored with a bare /dashboard or /super-admin link;
// derive the section from the activity type instead of sending them to the
// root dashboard.
const ACTIVITY_LINKS = {
  pipeline: "/dashboard?section=revenue-intel&ri_tab=pipeline",
  outreach: "/dashboard?section=revenue-intel&ri_tab=agent",
  "revenue-briefing": "/dashboard?section=revenue-intel&ri_tab=agent",
  "revenue-outcomes": "/dashboard?section=revenue-intel&ri_tab=agent",
  "company-research": "/dashboard?section=revenue-intel&ri_tab=agent",
  "channel-actions": "/dashboard?section=revenue-intel&ri_tab=agent",
  forecast: "/dashboard?section=revenue-intel&ri_tab=forecast",
  "ai-performance": "/dashboard?section=revenue-intel&ri_tab=performance",
  companies: "/dashboard",
  profile: "/dashboard",
  connections: "/dashboard",
  "api-keys": "/dashboard",
  billing: "/dashboard",
  estate: "/dashboard",
};

const isActivity = (notification) =>
  String(notification?.type || "").startsWith("activity.");

const openLinkFor = (notification) => {
  if (!notification) return null;
  if (isActivity(notification)) {
    const shouldDerive =
      !notification.link ||
      notification.link === "/dashboard" ||
      notification.link === "/super-admin";
    if (shouldDerive) {
      const resourceKey = String(notification.type).split(".")[1];
      return (
        ACTIVITY_LINKS[resourceKey] || notification.link || "/dashboard"
      );
    }
  }
  return notification.link || null;
};

const recipientLabelFor = (notification) => {
  if (!notification) return "—";
  if (notification.recipient) return notification.recipient;
  if (notification.category === "activity") return "Workspace users";
  if (notification.category === "system") return "Targeted workspaces";
  return "—";
};

const styles = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  .dash-root { min-height: 100vh; background: var(--bg); color: var(--text); font-family: var(--sans); font-size: 14px; line-height: 1.5; -webkit-font-smoothing: antialiased; }
  .dash-root.dark .dash-header { box-shadow: 0 1px 0 rgba(255,255,255,0.03), 0 2px 20px rgba(0,0,0,0.6); }

  .dash-header { background: #082a58; border-bottom: 1px solid rgba(255,255,255,0.07); position: sticky; top: 0; z-index: 100; }
  .dash-header-inner { max-width: 1560px; margin: 0 auto; padding: 0 28px; height: 64px; display: flex; align-items: center; justify-content: space-between; gap: 16px; }
  .dash-brand { display: flex; align-items: center; gap: 12px; flex-shrink: 0; }
  .btn-back { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.12); border-radius: var(--radius-sm); color: rgba(255,255,255,0.85); transition: all var(--t); margin-right: 4px; text-decoration: none; }
  .btn-back:hover { background: rgba(255,255,255,0.13); border-color: rgba(255,255,255,0.22); color: #fff; transform: translateX(-2px); }
  .dash-brand-text { display: flex; flex-direction: column; gap: 1px; }
  .dash-brand-name { font-size: 13.5px; font-weight: 600; color: #fff; letter-spacing: -0.01em; }
  .dash-brand-sub { font-family: var(--mono); font-size: 9px; letter-spacing: 0.1em; text-transform: uppercase; color: rgba(255,255,255,0.38); }
  .dash-header-right { display: flex; align-items: center; gap: 8px; }
  .btn-header { display: inline-flex; align-items: center; justify-content: center; gap: 6px; min-height: 36px; padding: 0 14px; background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.12); border-radius: 8px; font-family: var(--sans); font-size: 12.5px; font-weight: 500; color: rgba(255,255,255,0.78); cursor: pointer; text-decoration: none; transition: all var(--t); }
  .btn-header:hover { background: rgba(255,255,255,0.13); border-color: rgba(255,255,255,0.22); color: #fff; }
  .user-pill { display: flex; align-items: center; gap: 7px; padding: 5px 12px; background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.12); border-radius: 100px; font-size: 12.5px; font-weight: 500; color: rgba(255,255,255,0.85); }
  .online-dot { width: 6px; height: 6px; border-radius: 50%; background: #34d399; box-shadow: 0 0 0 2px rgba(52,211,153,0.25); flex-shrink: 0; }
  .settings-wrap { position: relative; }
  .settings-panel { display: none; position: absolute; right: 0; top: calc(100% + 8px); min-width: 210px; background: #0d2d5a; border: 1px solid rgba(255,255,255,0.1); border-radius: var(--radius); box-shadow: 0 12px 40px rgba(0,0,0,0.5); z-index: 200; padding: 4px; }
  .settings-panel.open { display: block; }
  .dash-root.dark .settings-panel { background: #1a1a24; border-color: var(--border); }
  .settings-row { display: flex; align-items: center; justify-content: space-between; padding: 9px 12px; font-size: 12.5px; color: rgba(255,255,255,0.7); border-radius: var(--radius-sm); cursor: pointer; transition: background var(--t); }
  .settings-row:hover { background: rgba(255,255,255,0.07); color: #fff; }

  .dash-main { max-width: 1560px; margin: 0 auto; padding: 28px; display: flex; flex-direction: column; gap: 18px; }
  .nc-heading { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
  .nc-heading-title { font-size: 24px; font-weight: 600; letter-spacing: -0.03em; color: var(--text); display: flex; align-items: center; gap: 10px; }
  .nc-heading-sub { margin-top: 4px; font-size: 13px; color: var(--text-muted); max-width: 720px; }
  .nc-heading-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }

  .nc-stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 10px; }
  .nc-stat { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 14px 16px; }
  .nc-stat-label { font-family: var(--mono); font-size: 9.5px; letter-spacing: 0.08em; text-transform: uppercase; color: var(--text-faint); }
  .nc-stat-value { margin-top: 5px; font-size: 22px; font-weight: 600; letter-spacing: -0.03em; color: var(--text); }
  .nc-stat.inbound .nc-stat-value { color: var(--green); }
  .nc-stat.outbound .nc-stat-value { color: var(--accent-mid); }
  .nc-stat.system .nc-stat-value { color: var(--purple); }
  .nc-stat.unread .nc-stat-value { color: var(--amber); }

  .nc-filters { display: flex; align-items: flex-end; gap: 10px; flex-wrap: wrap; }
  .nc-filter-select { width: 180px; }
  .nc-filter-search { width: 280px; max-width: 100%; }
  .nc-filter-switch { display: inline-flex; align-items: center; padding-bottom: 7px; }
  .nc-filter-spacer { flex: 1; }

  .table-wrap { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-lg); box-shadow: var(--shadow-sm); overflow: hidden; }
  .table-toolbar { display: flex; align-items: center; justify-content: space-between; padding: 8px 14px; border-bottom: 1px solid var(--border); background: var(--surface-raised); gap: 10px; flex-wrap: wrap; }
  .toolbar-label { font-family: var(--mono); font-size: 10.5px; color: var(--text-faint); letter-spacing: 0.04em; }
  .qa-card { padding: 16px 18px; display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; border-bottom: 1px solid var(--border); transition: background-color 0.12s ease; cursor: pointer; }
  .qa-card:last-child { border-bottom: none; }
  .qa-card:hover { background-color: var(--surface-hover); }
  .qa-card.unread { background: var(--accent-dim); }
  .qa-card.unread:hover { background: var(--accent-dim-strong); }
  .qa-card-info { min-width: 0; flex: 1; display: flex; gap: 13px; align-items: flex-start; }
  .nc-icon { width: 34px; height: 34px; border-radius: 9px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .nc-icon.inbound { background: var(--green-dim); color: var(--green); }
  .nc-icon.outbound { background: var(--accent-dim); color: var(--accent-mid); }
  .nc-icon.system { background: var(--purple-dim); color: var(--purple); }
  .nc-icon.warning { background: var(--amber-dim); color: var(--amber); }
  .nc-icon.error { background: var(--red-dim); color: var(--red); }
  .nc-card-body { min-width: 0; flex: 1; }
  .qa-card-name { font-size: 13.5px; font-weight: 600; color: var(--text); letter-spacing: -0.02em; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  .qa-card-sub { font-size: 12.5px; color: var(--text-muted); margin-top: 3px; line-height: 1.55; overflow-wrap: anywhere; }
  .qa-card-meta { font-family: var(--mono); font-size: 10.5px; color: var(--text-faint); margin-top: 6px; display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
  .qa-card-actions { display: flex; align-items: center; gap: 6px; flex-shrink: 0; flex-wrap: wrap; }
  .nc-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--amber); flex-shrink: 0; }

  .pill { display: inline-flex; align-items: center; gap: 5px; padding: 2px 8px; border-radius: 100px; font-family: var(--mono); font-size: 9.5px; font-weight: 500; letter-spacing: 0.04em; text-transform: uppercase; }
  .pill::before { content: ''; width: 5px; height: 5px; border-radius: 50%; }
  .pill.inbound { background: var(--green-dim); color: var(--green); border: 1px solid var(--green-border); }
  .pill.inbound::before { background: var(--green); }
  .pill.outbound { background: var(--accent-dim); color: var(--accent-mid); border: 1px solid var(--accent-glow); }
  .pill.outbound::before { background: var(--accent-mid); }
  .pill.system { background: var(--purple-dim); color: var(--purple); border: 1px solid rgba(139,92,246,0.25); }
  .pill.system::before { background: var(--purple); }
  .pill.failed { background: var(--red-dim); color: var(--red); border: 1px solid var(--red-border); }
  .pill.failed::before { background: var(--red); }
  .pill.cat { background: var(--surface-sub); color: var(--text-muted); border: 1px solid var(--border); }
  .pill.cat::before { display: none; }

  .empty-state { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 64px 20px; gap: 12px; text-align: center; }
  .empty-state-label { font-family: var(--mono); font-size: 11px; letter-spacing: 0.05em; text-transform: uppercase; color: var(--text-faint); }
  .nc-empty-title { font-size: 14px; font-weight: 600; color: var(--text); }
  .nc-empty-sub { font-size: 12.5px; color: var(--text-muted); max-width: 380px; }
  .spinner { width: 28px; height: 28px; border: 2px solid var(--border); border-top-color: var(--accent-mid); border-radius: 50%; animation: spin 0.7s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }
  .qa-notes-box { margin-top: 8px; padding: 8px 12px; background: var(--red-dim); border: 1px solid var(--red-border); border-radius: var(--radius-sm); font-size: 12px; color: var(--red); }

  .pagination { display: flex; align-items: center; justify-content: space-between; padding: 10px 14px; border-top: 1px solid var(--border); background: var(--surface-raised); gap: 10px; flex-wrap: wrap; }
  .page-info { font-family: var(--mono); font-size: 10.5px; color: var(--text-faint); letter-spacing: 0.04em; }
  .page-info b { color: var(--text-muted); font-weight: 500; }
  .page-controls { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }

  .nc-detail-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 10px; margin-bottom: 14px; }
  .nc-detail-kv { background: var(--surface-sunken); border-radius: var(--radius); padding: 10px 12px; min-width: 0; }
  .nc-detail-k { font-family: var(--mono); font-size: 9px; letter-spacing: 0.06em; text-transform: uppercase; color: var(--text-faint); }
  .nc-detail-v { margin-top: 4px; font-size: 12.5px; font-weight: 500; color: var(--text); overflow-wrap: anywhere; }
  .nc-detail-message { padding: 13px 15px; background: var(--surface-sunken); border-radius: var(--radius); font-size: 13px; line-height: 1.65; color: var(--text-muted); white-space: pre-wrap; overflow-wrap: anywhere; margin-bottom: 14px; }
  .nc-meta-block { background: var(--surface-sunken); border-radius: var(--radius); padding: 12px 14px; margin-bottom: 14px; }
  .nc-meta-title { font-family: var(--mono); font-size: 9px; letter-spacing: 0.06em; text-transform: uppercase; color: var(--text-faint); margin-bottom: 8px; }
  .nc-meta-row { display: flex; justify-content: space-between; gap: 12px; padding: 4px 0; border-bottom: 1px solid var(--border); font-size: 12px; }
  .nc-meta-row:last-child { border-bottom: none; }
  .nc-meta-key { color: var(--text-muted); flex-shrink: 0; }
  .nc-meta-val { color: var(--text); text-align: right; overflow-wrap: anywhere; font-family: var(--mono); font-size: 11.5px; }
  .nc-dialog-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 8px; flex-wrap: wrap; }
  .nc-form { display: flex; flex-direction: column; gap: 14px; }
  .nc-form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }

  @media (max-width: 720px) {
    .dash-header-inner { padding: 0 16px; }
    .dash-main { padding: 20px 16px 40px; }
    .qa-card { flex-direction: column; align-items: stretch; }
    .qa-card-actions { width: 100%; justify-content: flex-start; }
    .nc-filter-select, .nc-filter-search { width: 100%; }
    .nc-form-row { grid-template-columns: 1fr; }
  }
`;

const formatRelative = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const diff = Date.now() - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: date.getFullYear() === new Date().getFullYear() ? undefined : "numeric",
  });
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const typeLabel = (type) => {
  if (!type) return "Notification";
  const labels = {
    "brief.submitted": "Brief received",
    "brief.submitted.email": "Brief notification",
    "brief.confirmation.email": "Client confirmation",
    "email.brief": "Brief link",
    "email.preview": "Preview email",
    "email.followup": "Follow-up email",
    "email.intro": "Introduction email",
    "email.payment_request": "Payment request",
    "email.site_live": "Site-live email",
    "email.revenue_briefing": "Revenue briefing",
    "email.outreach_draft": "Outreach email",
    "email.partner_outreach": "Partner outreach",
    "email.welcome": "Welcome email",
    "email.cancellation_request": "Cancellation request",
    "email.upgrade_request": "Upgrade request",
    "email.password_reset": "Password reset",
    "email.activation_request": "Activation request",
    "email.feedback": "Feedback email",
    "email.ops_alert": "Ops alert",
    "reply.received": "Reply received",
    "website.build_started": "Build started",
    "website.ready": "Build ready",
    "website.qa_pending": "Awaiting QA",
    "website.build_failed": "Build failed",
    "qa.approved": "QA approved",
    "qa.regeneration_requested": "Regeneration requested",
    "payment.received": "Payment received",
    "outreach.channel_action": "Outreach touch",
    "system.announcement": "Announcement",
    "system.release": "System update",
    "user.invited": "User invited",
    "user.removed": "User removed",
    "user.role_changed": "Role changed",
    "user.password_reset": "Password reset",
    "user.retired": "User retired",
    "customer.created": "Customer created",
    "customer.removed": "Customer removed",
    "workspace.companies_assigned": "Companies assigned",
  };
  if (type.startsWith("activity.")) return "User activity";
  return labels[type] || type.replace(/[._]/g, " ");
};

const iconFor = (notification) => {
  if (notification.severity === "error") return "error";
  if (notification.severity === "warning") return "warning";
  if (notification.direction === "inbound") return "inbound";
  if (notification.direction === "outbound") return "outbound";
  return "system";
};

const DirectionIcon = ({ notification }) => {
  const kind = iconFor(notification);
  if (kind === "inbound") return <ArrowDownLeft size={17} />;
  if (kind === "outbound") return <ArrowUpRight size={17} />;
  return <Info size={17} />;
};

export default function Notifications() {
  const { user, logout } = useAuth();
  const { toast } = useToastStack();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    totalPages: 1,
  });
  const [counts, setCounts] = useState({
    total: 0,
    unread: 0,
    inbound: 0,
    outbound: 0,
    system: 0,
    byCategory: {},
  });
  const [categories, setCategories] = useState([]);
  const [severities, setSeverities] = useState([]);
  const [direction, setDirection] = useState("all");
  const [category, setCategory] = useState("all");
  const [severity, setSeverity] = useState("all");
  const [planFilter, setPlanFilter] = useState("all");
  const [customerFilter, setCustomerFilter] = useState("all");
  const [customerOptions, setCustomerOptions] = useState([
    { value: "all", label: "All customers" },
  ]);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);
  const [announceOpen, setAnnounceOpen] = useState(false);
  const [announcement, setAnnouncement] = useState({
    title: "",
    message: "",
    category: "system",
    severity: "info",
    audience: "all",
    link: "",
  });
  const [announceBusy, setAnnounceBusy] = useState(false);
  const [announceError, setAnnounceError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchDebounce, setSearchDebounce] = useState("");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(
    () => localStorage.getItem("axo_theme") === "dark",
  );

  const backTo = user?.isSuperAdmin ? "/super-admin" : "/dashboard";
  const isSuperAdmin = Boolean(user?.isSuperAdmin);

  useEffect(() => {
    localStorage.setItem("axo_theme", darkMode ? "dark" : "light");
  }, [darkMode]);

  useEffect(() => {
    if (!settingsOpen) return;
    const handler = () => setSettingsOpen(false);
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [settingsOpen]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounce(searchQuery.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    let cancelled = false;
    apiService
      .getCustomers()
      .then((res) => {
        if (cancelled) return;
        const rows = res.data || [];
        setCustomerOptions([
          { value: "all", label: "All customers" },
          ...rows
            .slice()
            .sort((a, b) => String(a.name).localeCompare(String(b.name)))
            .map((customer) => ({
              value: customer.id,
              label: customer.name,
            })),
        ]);
      })
      .catch(() => {
        // The customer filter is additive; leave the default option only.
      });
    return () => {
      cancelled = true;
    };
  }, [isSuperAdmin]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = { page, limit: PAGE_SIZE };
      if (direction !== "all") params.direction = direction;
      if (category !== "all") params.category = category;
      if (severity !== "all") params.severity = severity;
      if (isSuperAdmin && customerFilter !== "all") {
        params.customerId = customerFilter;
      }
      if (isSuperAdmin && planFilter !== "all") params.planId = planFilter;
      if (unreadOnly) params.unread = "true";
      if (searchDebounce) params.q = searchDebounce;
      const res = await apiService.getNotifications(params);
      setNotifications(res.data || []);
      setPagination(res.pagination || { total: 0, page, totalPages: 1 });
      if (res.counts) setCounts((c) => ({ ...c, ...res.counts }));
      if (Array.isArray(res.categories)) setCategories(res.categories);
      if (Array.isArray(res.severities)) setSeverities(res.severities);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load notifications.");
    } finally {
      setLoading(false);
    }
  }, [
    page,
    direction,
    category,
    severity,
    planFilter,
    customerFilter,
    unreadOnly,
    searchDebounce,
    isSuperAdmin,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  const markRead = async (notification) => {
    if (notification.readAt) return;
    setNotifications((rows) =>
      rows.map((row) =>
        row.id === notification.id
          ? { ...row, readAt: new Date().toISOString() }
          : row,
      ),
    );
    setCounts((c) => ({ ...c, unread: Math.max(0, c.unread - 1) }));
    try {
      await apiService.markNotificationRead(notification.id);
    } catch (err) {
      console.error("Failed to mark notification read:", err);
    }
  };

  const markAllRead = async () => {
    if (counts.unread === 0) return;
    setBusy(true);
    try {
      await apiService.markAllNotificationsRead();
      setNotifications((rows) =>
        rows.map((row) => ({
          ...row,
          readAt: row.readAt || new Date().toISOString(),
        })),
      );
      setCounts((c) => ({ ...c, unread: 0 }));
      toast({ title: "All notifications marked as read", type: "success" });
    } catch (err) {
      toast({
        title: err.response?.data?.message || "Failed to mark all as read.",
        type: "error",
      });
    } finally {
      setBusy(false);
    }
  };

  const openDetail = (notification) => {
    setSelected(notification);
    markRead(notification);
  };

  const changeDirection = (next) => {
    setDirection(next);
    setPage(1);
  };

  const submitAnnouncement = async (e) => {
    e.preventDefault();
    if (!announcement.title.trim()) {
      setAnnounceError("Title is required.");
      return;
    }
    setAnnounceBusy(true);
    setAnnounceError("");
    try {
      const audience = AUDIENCE_OPTIONS.find(
        (option) => option.value === announcement.audience,
      );
      await apiService.createNotificationAnnouncement({
        title: announcement.title.trim(),
        message: announcement.message.trim() || null,
        category: announcement.category,
        severity: announcement.severity,
        requiredCapability: audience?.capability || null,
        link: announcement.link.trim() || null,
      });
      setAnnounceOpen(false);
      setAnnouncement({
        title: "",
        message: "",
        category: "system",
        severity: "info",
        audience: "all",
        link: "",
      });
      setPage(1);
      await load();
      toast({ title: "Announcement sent to all workspaces", type: "success" });
    } catch (err) {
      setAnnounceError(
        err.response?.data?.message || "Failed to create announcement.",
      );
    } finally {
      setAnnounceBusy(false);
    }
  };

  const categoryOptions = useMemo(() => {
    const values = categories.length
      ? categories
      : Object.keys(CATEGORY_LABELS).filter((key) => key !== "all");
    return [
      { value: "all", label: CATEGORY_LABELS.all },
      ...values.map((value) => ({
        value,
        label: CATEGORY_LABELS[value] || value,
      })),
    ];
  }, [categories]);

  const severityOptions = useMemo(() => {
    const values = severities.length
      ? severities
      : Object.keys(SEVERITY_LABELS).filter((key) => key !== "all");
    return [
      { value: "all", label: SEVERITY_LABELS.all },
      ...values.map((value) => ({
        value,
        label: SEVERITY_LABELS[value] || value,
      })),
    ];
  }, [severities]);

  const announcementCategoryOptions = categoryOptions.filter(
    (option) => option.value !== "all",
  );
  const announcementSeverityOptions = severityOptions.filter(
    (option) => option.value !== "all",
  );

  return (
    <div className={`dash-root${darkMode ? " dark" : ""}`}>
      <style>{styles}</style>

      <header className="dash-header">
        <div className="dash-header-inner">
          <div className="dash-brand">
            <Link to={backTo} className="btn-back" title="Back to Dashboard">
              <svg
                width="18"
                height="18"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </Link>
            <div className="dash-brand-text">
              <div className="dash-brand-name">Notification Centre</div>
              <div className="dash-brand-sub">Axo Signal</div>
            </div>
          </div>

          <div className="dash-header-right">
            <div
              className="settings-wrap"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="btn-header"
                onClick={() => setSettingsOpen((o) => !o)}
              >
                <Settings size={14} />
                Settings
              </button>
              <div className={`settings-panel${settingsOpen ? " open" : ""}`}>
                <div className="settings-row">
                  <span>Dark mode</span>
                  <ThemeSwitch
                    iconOnly
                    theme={darkMode ? "dark" : "light"}
                    onThemeChange={(next, variant, trigger) =>
                      applyThemeWithTransition(
                        next,
                        trigger,
                        setDarkMode,
                        variant,
                      )
                    }
                  />
                </div>
              </div>
            </div>
            <div className="user-pill">
              <div className="online-dot" />
              {user?.name}
            </div>
            <button onClick={logout} className="btn-header">
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="dash-main">
        <div className="nc-heading">
          <div>
            <div className="nc-heading-title">
              <Bell size={22} /> Notifications
              {user?.customer && (
                <span className="pill cat">
                  {customerPlanLabel(user.customer)} plan
                </span>
              )}
            </div>
            <div className="nc-heading-sub">
              Every brief, reply, email, website build, payment, user change
              and system update in one place — inbound, outbound and internal.
              Your plan decides which modules appear here.
            </div>
          </div>
          <div className="nc-heading-actions">
            {isSuperAdmin && (
              <Button
                variant="secondary"
                size="md"
                onClick={() => {
                  setAnnounceError("");
                  setAnnounceOpen(true);
                }}
              >
                <Megaphone size={14} /> Announce
              </Button>
            )}
            <Button variant="secondary" size="md" onClick={load} disabled={loading}>
              <RefreshCw size={14} /> Refresh
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={markAllRead}
              disabled={busy || counts.unread === 0}
              loading={busy}
            >
              <Check size={14} /> Mark all read
            </Button>
          </div>
        </div>

        <div className="nc-stats">
          <div className="nc-stat">
            <div className="nc-stat-label">Total</div>
            <div className="nc-stat-value">{counts.total}</div>
          </div>
          <div className="nc-stat inbound">
            <div className="nc-stat-label">Inbound</div>
            <div className="nc-stat-value">{counts.inbound}</div>
          </div>
          <div className="nc-stat outbound">
            <div className="nc-stat-label">Outbound</div>
            <div className="nc-stat-value">{counts.outbound}</div>
          </div>
          <div className="nc-stat system">
            <div className="nc-stat-label">System</div>
            <div className="nc-stat-value">{counts.system}</div>
          </div>
          <div className="nc-stat unread">
            <div className="nc-stat-label">Unread</div>
            <div className="nc-stat-value">{counts.unread}</div>
          </div>
        </div>

        <Tabs value={direction} onValueChange={changeDirection}>
          <TabsList aria-label="Notification direction">
            {["all", "inbound", "outbound", "system"].map((value) => (
              <TabsTrigger key={value} value={value}>
                {DIRECTION_LABELS[value]}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className="nc-filters">
          <div className="nc-filter-select">
            <Select
              label="Category"
              hideLabel
              value={category}
              onValueChange={(next) => {
                setCategory(next);
                setPage(1);
              }}
              options={categoryOptions}
            />
          </div>
          <div className="nc-filter-select">
            <Select
              label="Severity"
              hideLabel
              value={severity}
              onValueChange={(next) => {
                setSeverity(next);
                setPage(1);
              }}
              options={severityOptions}
            />
          </div>
          {isSuperAdmin && (
            <div className="nc-filter-select">
              <Select
                label="Customer"
                hideLabel
                value={customerFilter}
                onValueChange={(next) => {
                  setCustomerFilter(next);
                  setPlanFilter("all");
                  setPage(1);
                }}
                options={customerOptions}
              />
            </div>
          )}
          {isSuperAdmin && (
            <div className="nc-filter-select">
              <Select
                label="Plan"
                hideLabel
                value={planFilter}
                onValueChange={(next) => {
                  setPlanFilter(next);
                  setPage(1);
                }}
                options={PLAN_FILTER_OPTIONS}
              />
            </div>
          )}
          <div className="nc-filter-search">
            <SearchField
              label="Search notifications"
              hideLabel
              value={searchQuery}
              onValueChange={setSearchQuery}
              placeholder="Search titles, messages, recipients…"
            />
          </div>
          <div className="nc-filter-spacer" />
          <div className="nc-filter-switch">
            <Switch
              checked={unreadOnly}
              onCheckedChange={(next) => {
                setUnreadOnly(next);
                setPage(1);
              }}
              label="Unread only"
            />
          </div>
        </div>

        {error && <div className="qa-notes-box">{error}</div>}

        <div className="table-wrap">
          {!loading && notifications.length > 0 && (
            <div className="table-toolbar">
              <span className="toolbar-label">
                {pagination.total.toLocaleString()} notification
                {pagination.total === 1 ? "" : "s"}
              </span>
            </div>
          )}

          {loading ? (
            <div className="empty-state" style={{ width: "100%" }}>
              <Skeleton lines={6} label="Loading notifications" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="empty-state">
              <div className="nc-empty-title">No notifications yet</div>
              <div className="nc-empty-sub">
                {searchDebounce
                  ? `No notifications match "${searchDebounce}".`
                  : "Activity from your workspace and the wider AXO Signal system will appear here as it happens."}
              </div>
            </div>
          ) : (
            notifications.map((notification) => {
              const isUnread = !notification.readAt;
              const kind = iconFor(notification);
              return (
                <div
                  key={notification.id}
                  className={`qa-card nc-card${isUnread ? " unread" : ""}`}
                  onClick={() => openDetail(notification)}
                >
                  <div className="qa-card-info">
                    <div className={`nc-icon ${kind}`}>
                      <DirectionIcon notification={notification} />
                    </div>
                    <div className="nc-card-body">
                      <div className="qa-card-name">
                        {isUnread && <span className="nc-dot" />}
                        {notification.title}
                        <span className={`pill ${notification.direction}`}>
                          {notification.direction}
                        </span>
                        {notification.category && (
                          <span className="pill cat">
                            {CATEGORY_LABELS[notification.category] ||
                              notification.category}
                          </span>
                        )}
                        {notification.status === "failed" && (
                          <span className="pill failed">failed</span>
                        )}
                      </div>
                      {notification.message && (
                        <div className="qa-card-sub">
                          {notification.message}
                        </div>
                      )}
                      <div className="qa-card-meta">
                        <span>{typeLabel(notification.type)}</span>
                        {notification.customer?.name && (
                          <span>
                            {notification.customer.name} ·{" "}
                            {customerPlanLabel(notification.customer)}
                          </span>
                        )}
                        {notification.actorName && (
                          <span>by {notification.actorName}</span>
                        )}
                        {(notification.recipient ||
                          notification.category === "activity") && (
                          <span>
                            {notification.recipient || "Workspace users"}
                          </span>
                        )}
                        <span>{formatRelative(notification.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="qa-card-actions">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        openDetail(notification);
                      }}
                    >
                      View
                    </Button>
                    {isUnread && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          markRead(notification);
                        }}
                      >
                        <Check size={12} /> Mark read
                      </Button>
                    )}
                  </div>
                </div>
              );
            })
          )}

          {!loading && notifications.length > 0 && (
            <div className="pagination">
              <div className="page-info">
                Showing <b>{(pagination.page - 1) * PAGE_SIZE + 1}</b>–
                <b>
                  {Math.min(pagination.page * PAGE_SIZE, pagination.total)}
                </b>{" "}
                of <b>{pagination.total}</b>
              </div>
              <div className="page-controls">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={pagination.page <= 1}
                >
                  <ChevronLeft size={14} /> Previous
                </Button>
                <span className="page-info">
                  Page <b>{pagination.page}</b> of{" "}
                  <b>{Math.max(1, pagination.totalPages)}</b>
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    setPage((p) =>
                      Math.min(pagination.totalPages || 1, p + 1),
                    )
                  }
                  disabled={pagination.page >= pagination.totalPages}
                >
                  Next <ChevronRight size={14} />
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>

      <Dialog
        open={Boolean(selected)}
        onOpenChange={(next) => {
          if (!next) setSelected(null);
        }}
      >
        {selected && (
          <DialogContent
            title={selected.title}
            description={`${typeLabel(selected.type)} · ${formatDateTime(
              selected.createdAt,
            )}`}
          >
            <div className="nc-detail-grid">
              <div className="nc-detail-kv">
                <div className="nc-detail-k">Direction</div>
                <div className="nc-detail-v">{selected.direction}</div>
              </div>
              <div className="nc-detail-kv">
                <div className="nc-detail-k">Category</div>
                <div className="nc-detail-v">
                  {CATEGORY_LABELS[selected.category] || selected.category}
                </div>
              </div>
              <div className="nc-detail-kv">
                <div className="nc-detail-k">Severity</div>
                <div className="nc-detail-v">
                  {SEVERITY_LABELS[selected.severity] || selected.severity}
                </div>
              </div>
              <div className="nc-detail-kv">
                <div className="nc-detail-k">Status</div>
                <div className="nc-detail-v">{selected.status}</div>
              </div>
              <div className="nc-detail-kv">
                <div className="nc-detail-k">Actor</div>
                <div className="nc-detail-v">{selected.actorName || "—"}</div>
              </div>
              <div className="nc-detail-kv">
                <div className="nc-detail-k">Recipient</div>
                <div className="nc-detail-v">
                  {recipientLabelFor(selected)}
                </div>
              </div>
              <div className="nc-detail-kv">
                <div className="nc-detail-k">Workspace</div>
                <div className="nc-detail-v">
                  {selected.customer?.name
                    ? `${selected.customer.name} · ${customerPlanLabel(
                        selected.customer,
                      )}`
                    : "Platform-wide"}
                </div>
              </div>
              <div className="nc-detail-kv">
                <div className="nc-detail-k">Workspace ID</div>
                <div className="nc-detail-v">
                  {selected.customerId || "Platform-wide"}
                </div>
              </div>
              <div className="nc-detail-kv">
                <div className="nc-detail-k">Visible to</div>
                <div className="nc-detail-v">
                  {capabilityAccessLabel(selected.requiredCapability)}
                </div>
              </div>
            </div>

            {selected.message && (
              <div className="nc-detail-message">{selected.message}</div>
            )}

            {selected.metadata && Object.keys(selected.metadata).length > 0 && (
              <div className="nc-meta-block">
                <div className="nc-meta-title">Full details</div>
                {Object.entries(selected.metadata).map(([key, value]) => (
                  <div className="nc-meta-row" key={key}>
                    <span className="nc-meta-key">{key}</span>
                    <span className="nc-meta-val">
                      {value == null
                        ? "—"
                        : typeof value === "object"
                          ? JSON.stringify(value)
                          : String(value)}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <div className="nc-dialog-actions">
              {openLinkFor(selected) && (
                <Button
                  variant="primary"
                  size="md"
                  onClick={() => {
                    const target = openLinkFor(selected);
                    setSelected(null);
                    navigate(target);
                  }}
                >
                  <ExternalLink size={14} /> Open
                </Button>
              )}
              <Button
                variant="secondary"
                size="md"
                onClick={() => setSelected(null)}
              >
                Close
              </Button>
            </div>
          </DialogContent>
        )}
      </Dialog>

      <Dialog open={announceOpen} onOpenChange={setAnnounceOpen}>
        <DialogContent
          title="New announcement"
          description="Broadcast a system notice to every active workspace."
        >
          <form className="nc-form" onSubmit={submitAnnouncement}>
            {announceError && <div className="qa-notes-box">{announceError}</div>}
            <Input
              label="Title"
              value={announcement.title}
              onChange={(e) =>
                setAnnouncement((a) => ({ ...a, title: e.target.value }))
              }
              placeholder="e.g. New pipeline forecasting is live"
            />
            <Textarea
              label="Message"
              value={announcement.message}
              onChange={(e) =>
                setAnnouncement((a) => ({ ...a, message: e.target.value }))
              }
              placeholder="What changed, and what the user should know."
            />
            <div className="nc-form-row">
              <Select
                label="Category"
                value={announcement.category}
                onValueChange={(next) =>
                  setAnnouncement((a) => ({ ...a, category: next }))
                }
                options={announcementCategoryOptions}
              />
              <Select
                label="Severity"
                value={announcement.severity}
                onValueChange={(next) =>
                  setAnnouncement((a) => ({ ...a, severity: next }))
                }
                options={announcementSeverityOptions}
              />
            </div>
            <Select
              label="Visible to"
              value={announcement.audience}
              onValueChange={(next) =>
                setAnnouncement((a) => ({ ...a, audience: next }))
              }
              options={AUDIENCE_OPTIONS.map(({ value, label }) => ({
                value,
                label,
              }))}
              description="Only workspaces on this plan (or above) will see the announcement."
            />
            <Input
              label="Link (optional)"
              value={announcement.link}
              onChange={(e) =>
                setAnnouncement((a) => ({ ...a, link: e.target.value }))
              }
              placeholder="/dashboard"
            />
            <div className="nc-dialog-actions">
              <Button
                type="button"
                variant="secondary"
                size="md"
                onClick={() => setAnnounceOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                size="md"
                loading={announceBusy}
              >
                <Megaphone size={14} /> Broadcast
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
